import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Upload, Download, FileSpreadsheet, CheckCircle2, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_CONFIG = {
  brand: { label: 'Parent Brand', color: 'blue' },
  sub_brand: { label: 'Sub Brand', color: 'indigo' },
  category: { label: 'Category', color: 'green' },
  menu: { label: 'Menu Group', color: 'purple' },
  sub_menu: { label: 'Sub Menu', color: 'orange' },
};

export default function MasterBulkUpload({ masterType, open, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [validating, setValidating] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [uploadMode, setUploadMode] = useState('add_new');
  const [executeResult, setExecuteResult] = useState(null);

  const config = TYPE_CONFIG[masterType] || TYPE_CONFIG.brand;

  function reset() {
    setStep(1);
    setFile(null);
    setValidating(false);
    setExecuting(false);
    setValidationResult(null);
    setUploadMode('add_new');
    setExecuteResult(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function downloadTemplate() {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(API + `/masters/bulk-upload/template/${masterType}`, {
        headers: { Authorization: 'Bearer ' + token },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `master_${masterType}_template.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (err) {
      toast.error('Failed to download template');
    }
  }

  async function validateFile() {
    if (!file) { toast.error('Please select a file'); return; }
    setValidating(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(API + `/masters/bulk-upload/validate/${masterType}`, formData, {
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'multipart/form-data' },
      });
      setValidationResult(res.data);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Validation failed');
    } finally {
      setValidating(false);
    }
  }

  async function executeUpload() {
    if (!validationResult || validationResult.valid_count === 0) return;
    setExecuting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(API + `/masters/bulk-upload/execute/${masterType}`, {
        rows: validationResult.valid_rows,
        mode: uploadMode,
      }, { headers: { Authorization: 'Bearer ' + token } });
      setExecuteResult(res.data);
      setStep(4);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setExecuting(false);
    }
  }

  return React.createElement(Dialog, { open: open, onOpenChange: handleClose },
    React.createElement(DialogContent, { className: 'max-w-lg' },
      React.createElement(DialogHeader, null,
        React.createElement(DialogTitle, { className: 'flex items-center gap-2' },
          React.createElement(FileSpreadsheet, { className: 'w-5 h-5' }),
          'Bulk Upload - ', config.label
        )
      ),
      React.createElement('div', { className: 'space-y-4' },
        // Step indicators
        React.createElement('div', { className: 'flex items-center gap-2 text-xs' },
          [1,2,3,4].map(s => React.createElement('div', {
            key: s,
            className: `flex items-center gap-1 ${step >= s ? 'text-blue-600 font-semibold' : 'text-gray-400'}`
          },
            React.createElement('div', {
              className: `w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${step >= s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`
            }, s),
            ['Template', 'Upload', 'Preview', 'Done'][s-1],
            s < 4 ? React.createElement('span', { className: 'mx-1 text-gray-300' }, '>') : null
          ))
        ),

        // Step 1: Download Template
        step === 1 && React.createElement('div', { className: 'space-y-3' },
          React.createElement('p', { className: 'text-sm text-gray-600' },
            'Download the Excel template, fill in your data, then upload.'
          ),
          React.createElement(Button, {
            onClick: downloadTemplate,
            variant: 'outline',
            className: 'w-full gap-2',
            'data-testid': 'download-master-template'
          }, React.createElement(Download, { className: 'w-4 h-4' }), 'Download Template'),
          React.createElement(CsvDownloadButton, { onDownload: downloadTemplate, testId: 'download-master-template-csv', label: 'Download Template (CSV)', size: 'default', className: 'w-full' }),
          React.createElement(Button, {
            onClick: () => setStep(2),
            className: 'w-full',
            'data-testid': 'proceed-to-upload'
          }, 'I have the file ready')
        ),

        // Step 2: Upload File
        step === 2 && React.createElement('div', { className: 'space-y-3' },
          React.createElement('div', {
            className: 'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition-colors',
            onClick: () => document.getElementById('master-upload-input').click()
          },
            React.createElement(Upload, { className: 'w-8 h-8 mx-auto text-gray-400 mb-2' }),
            file
              ? React.createElement('p', { className: 'text-sm font-medium text-green-700' }, file.name)
              : React.createElement('p', { className: 'text-sm text-gray-500' }, 'Click to select Excel or CSV file (.xlsx, .csv)')
          ),
          React.createElement('input', {
            id: 'master-upload-input',
            type: 'file',
            accept: '.xlsx,.xls,.csv',
            className: 'hidden',
            onChange: (e) => { if (e.target.files[0]) setFile(e.target.files[0]); }
          }),
          // Upload mode
          React.createElement('div', { className: 'space-y-1' },
            React.createElement('label', { className: 'text-xs font-medium text-gray-600' }, 'Upload Mode'),
            React.createElement('div', { className: 'flex gap-2' },
              React.createElement('button', {
                className: `flex-1 text-xs py-2 px-3 rounded border ${uploadMode === 'add_new' ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-white border-gray-200'}`,
                onClick: () => setUploadMode('add_new'),
                'data-testid': 'mode-add-new'
              }, 'Add New Only'),
              React.createElement('button', {
                className: `flex-1 text-xs py-2 px-3 rounded border ${uploadMode === 'update_existing' ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-white border-gray-200'}`,
                onClick: () => setUploadMode('update_existing'),
                'data-testid': 'mode-update-existing'
              }, 'Update Existing')
            )
          ),
          React.createElement('div', { className: 'flex gap-2' },
            React.createElement(Button, { variant: 'outline', onClick: () => setStep(1), className: 'flex-1' }, 'Back'),
            React.createElement(Button, {
              onClick: validateFile,
              disabled: !file || validating,
              className: 'flex-1 gap-1',
              'data-testid': 'validate-upload-btn'
            },
              validating ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : null,
              validating ? 'Validating...' : 'Validate & Preview'
            )
          )
        ),

        // Step 3: Validation Preview
        step === 3 && validationResult && React.createElement('div', { className: 'space-y-3' },
          // Summary cards
          React.createElement('div', { className: 'grid grid-cols-3 gap-2' },
            React.createElement('div', { className: 'bg-gray-50 rounded p-3 text-center' },
              React.createElement('div', { className: 'text-lg font-bold' }, validationResult.total),
              React.createElement('div', { className: 'text-xs text-gray-500' }, 'Total Rows')
            ),
            React.createElement('div', { className: 'bg-green-50 rounded p-3 text-center' },
              React.createElement('div', { className: 'text-lg font-bold text-green-700' }, validationResult.valid_count),
              React.createElement('div', { className: 'text-xs text-green-600' }, 'Valid')
            ),
            React.createElement('div', { className: 'bg-red-50 rounded p-3 text-center' },
              React.createElement('div', { className: 'text-lg font-bold text-red-700' }, validationResult.error_count),
              React.createElement('div', { className: 'text-xs text-red-600' }, 'Errors')
            )
          ),
          // Errors list
          validationResult.error_count > 0 && React.createElement('div', { className: 'border rounded max-h-40 overflow-y-auto' },
            React.createElement('div', { className: 'px-3 py-1.5 bg-red-50 text-xs font-semibold text-red-700 sticky top-0' }, 'Errors'),
            validationResult.error_rows.map((err, i) => React.createElement('div', {
              key: i,
              className: 'px-3 py-1.5 text-xs border-t flex justify-between'
            },
              React.createElement('span', { className: 'text-gray-600' }, 'Row ', err.row, ': ', Object.values(err.data || {}).filter(Boolean).join(' / ')),
              React.createElement('span', { className: 'text-red-600 font-medium' }, err.error)
            ))
          ),
          // Valid rows preview
          validationResult.valid_count > 0 && React.createElement('div', { className: 'border rounded max-h-32 overflow-y-auto' },
            React.createElement('div', { className: 'px-3 py-1.5 bg-green-50 text-xs font-semibold text-green-700 sticky top-0' }, 'Valid Records'),
            validationResult.valid_rows.slice(0, 10).map((row, i) => React.createElement('div', {
              key: i,
              className: 'px-3 py-1 text-xs border-t text-gray-700'
            }, row.brand_name ? `${row.brand_name} > ${row.name}` : row.category_name ? `${row.category_name} > ${row.name}` : row.name)),
            validationResult.valid_count > 10 && React.createElement('div', { className: 'px-3 py-1 text-xs text-gray-400 border-t' }, `... and ${validationResult.valid_count - 10} more`)
          ),
          // Actions
          React.createElement('div', { className: 'flex gap-2' },
            React.createElement(Button, { variant: 'outline', onClick: () => { setStep(2); setValidationResult(null); }, className: 'flex-1' }, 'Re-upload'),
            React.createElement(Button, {
              onClick: executeUpload,
              disabled: validationResult.valid_count === 0 || executing,
              className: 'flex-1 gap-1',
              'data-testid': 'confirm-upload-btn'
            },
              executing ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(CheckCircle2, { className: 'w-4 h-4' }),
              executing ? 'Uploading...' : `Upload ${validationResult.valid_count} Records`
            )
          )
        ),

        // Step 4: Done
        step === 4 && executeResult && React.createElement('div', { className: 'space-y-3 text-center' },
          React.createElement(CheckCircle2, { className: 'w-12 h-12 mx-auto text-green-500' }),
          React.createElement('h3', { className: 'font-semibold text-lg' }, 'Upload Complete!'),
          React.createElement('div', { className: 'grid grid-cols-2 gap-2 text-sm' },
            React.createElement('div', { className: 'bg-green-50 rounded p-2' },
              React.createElement('div', { className: 'font-bold text-green-700' }, executeResult.added),
              React.createElement('div', { className: 'text-xs text-green-600' }, 'Added')
            ),
            React.createElement('div', { className: 'bg-blue-50 rounded p-2' },
              React.createElement('div', { className: 'font-bold text-blue-700' }, executeResult.updated),
              React.createElement('div', { className: 'text-xs text-blue-600' }, 'Updated')
            ),
            React.createElement('div', { className: 'bg-yellow-50 rounded p-2' },
              React.createElement('div', { className: 'font-bold text-yellow-700' }, executeResult.skipped),
              React.createElement('div', { className: 'text-xs text-yellow-600' }, 'Skipped')
            ),
            React.createElement('div', { className: 'bg-red-50 rounded p-2' },
              React.createElement('div', { className: 'font-bold text-red-700' }, executeResult.failed),
              React.createElement('div', { className: 'text-xs text-red-600' }, 'Failed')
            )
          ),
          React.createElement(Button, { onClick: handleClose, className: 'w-full mt-2', 'data-testid': 'close-upload-btn' }, 'Done')
        )
      )
    )
  );
}
